import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  Loader2,
  Plus,
  Send,
  Sparkles,
  Trash2,
  Briefcase,
  User as UserIcon,
  FileText,
  Search,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { api, ApiError } from '../lib/api'
import { streamChat } from '../lib/aiStream'
import {
  AiContextTypeLabel,
  type AiConversationDetail,
  type AiConversationListItem,
  type AiConversationListResponse,
  type AiStatusResponse,
  type AiContextTypeT,
  type CaseListResponse,
  type ChatReference,
  type ClientListResponse,
} from '@toga/shared'
import {
  CitationDrawer,
  renderWithCitations,
  useCitationDrawer,
} from '../components/ai/Citations'

interface UiMessage {
  id: string
  role: 'USER' | 'ASSISTANT'
  content: string
  pending?: boolean
  references?: ChatReference[]
}

interface ContextSelection {
  type: AiContextTypeT
  id: string | null
  label: string | null
}

export default function AIAssistant() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [status, setStatus] = useState<AiStatusResponse | null>(null)
  const [conversations, setConversations] = useState<AiConversationListItem[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [messages, setMessages] = useState<UiMessage[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [contextSel, setContextSel] = useState<ContextSelection>({
    type: 'NONE',
    id: null,
    label: null,
  })
  const [showContext, setShowContext] = useState(false)
  const messagesRef = useRef<HTMLDivElement | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const drawer = useCitationDrawer()

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeId) ?? null,
    [conversations, activeId]
  )

  // ----- Carga inicial -----

  const loadStatus = useCallback(async () => {
    try {
      const s = await api.get<AiStatusResponse>('/ai/status')
      setStatus(s)
    } catch {
      setStatus({ available: false, model: '', ragEnabled: false, embeddingModel: null })
    }
  }, [])

  const loadConversations = useCallback(async () => {
    try {
      const resp = await api.get<AiConversationListResponse>('/ai/conversations')
      setConversations(resp.items)
    } catch {
      setConversations([])
    }
  }, [])

  useEffect(() => {
    void loadStatus()
    void loadConversations()
  }, [loadStatus, loadConversations])

  // Si llegamos con ?conversation=ID en la URL, abrir esa conversación.
  useEffect(() => {
    const cid = searchParams.get('conversation')
    if (cid && cid !== activeId) {
      setActiveId(cid)
      void loadConversation(cid)
      // Limpiar el query string para que el botón "Nueva conversación" funcione luego
      const next = new URLSearchParams(searchParams)
      next.delete('conversation')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const loadConversation = useCallback(async (id: string) => {
    try {
      const { conversation } = await api.get<{ conversation: AiConversationDetail }>(
        `/ai/conversations/${id}`
      )
      setMessages(
        conversation.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
        }))
      )
      setContextSel({
        type: conversation.contextType,
        id: conversation.contextId,
        label: conversation.contextLabel,
      })
    } catch {
      setMessages([])
    }
  }, [])

  const onSelectConversation = useCallback(
    (id: string) => {
      setActiveId(id)
      setError(null)
      void loadConversation(id)
    },
    [loadConversation]
  )

  const onNewConversation = useCallback(() => {
    abortRef.current?.abort()
    setActiveId(null)
    setMessages([])
    setError(null)
    setContextSel({ type: 'NONE', id: null, label: null })
    setInput('')
  }, [])

  const onDeleteConversation = useCallback(
    async (id: string) => {
      if (!confirm('¿Eliminar esta conversación?')) return
      try {
        await api.delete(`/ai/conversations/${id}`)
        if (id === activeId) onNewConversation()
        await loadConversations()
      } catch (err) {
        if (err instanceof ApiError) setError(err.message)
      }
    },
    [activeId, loadConversations, onNewConversation]
  )

  // ----- Auto-scroll -----

  useEffect(() => {
    const el = messagesRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages, streaming])

  // ----- Envío -----

  const onSend = useCallback(async () => {
    const text = input.trim()
    if (!text || streaming || !status?.available) return

    setInput('')
    setError(null)
    setStreaming(true)

    const userMsg: UiMessage = {
      id: `local-${Date.now()}`,
      role: 'USER',
      content: text,
    }
    const assistantMsg: UiMessage = {
      id: `local-assistant-${Date.now()}`,
      role: 'ASSISTANT',
      content: '',
      pending: true,
    }
    setMessages((prev) => [...prev, userMsg, assistantMsg])

    const ctrl = new AbortController()
    abortRef.current = ctrl
    let convId: string | null = activeId

    try {
      await streamChat(
        {
          conversationId: activeId ?? undefined,
          message: text,
          contextType: !activeId ? contextSel.type : undefined,
          contextId: !activeId && contextSel.id ? contextSel.id : undefined,
        },
        (event) => {
          if (event.type === 'start') {
            convId = event.conversationId
            if (!activeId) setActiveId(event.conversationId)
          } else if (event.type === 'delta') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? { ...m, content: m.content + event.text, pending: true }
                  : m
              )
            )
          } else if (event.type === 'done') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? { ...m, pending: false, references: event.references }
                  : m
              )
            )
            convId = event.conversationId
          } else if (event.type === 'error') {
            setError(event.message)
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantMsg.id ? { ...m, pending: false } : m))
            )
          }
        },
        ctrl.signal
      )
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError((err as Error).message ?? 'Error al conectar con la IA')
      }
    } finally {
      setStreaming(false)
      abortRef.current = null
      await loadConversations()
      // recargar mensajes "reales" del servidor para tener IDs y métricas
      if (convId) await loadConversation(convId)
    }
  }, [
    activeId,
    contextSel.id,
    contextSel.type,
    input,
    loadConversation,
    loadConversations,
    status?.available,
    streaming,
  ])

  const onCancel = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] animate-slideUp">
      <header className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-toga-400" />
          <h1 className="text-xl font-serif font-semibold t-primary">Asistente IA</h1>
          {status && (
            <span
              className={[
                'ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded',
                status.available
                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                  : 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
              ].join(' ')}
            >
              {status.available ? `Activo · ${status.model}` : 'No configurado'}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onNewConversation}
          className="px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
        >
          <Plus size={14} /> Nueva conversación
        </button>
      </header>

      <div className="grid grid-cols-[260px_1fr] gap-3 flex-1 min-h-0">
        {/* Sidebar conversaciones */}
        <aside className="surface p-2 overflow-y-auto">
          {conversations.length === 0 ? (
            <p className="text-xs t-faint px-2 py-4 text-center">Sin conversaciones todavía.</p>
          ) : (
            <ul className="space-y-1">
              {conversations.map((c) => {
                const active = c.id === activeId
                return (
                  <li key={c.id}>
                    <div
                      className={[
                        'group flex items-start gap-1 rounded-lg px-2 py-1.5 cursor-pointer',
                        active ? 'bg-toga-500/15 border border-toga-500/30' : 'hover:bg-hover',
                      ].join(' ')}
                      onClick={() => onSelectConversation(c.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm t-primary truncate">{c.title}</div>
                        {c.contextLabel && (
                          <div className="text-[10px] t-faint truncate flex items-center gap-1">
                            <ContextIcon type={c.contextType} />
                            {c.contextLabel}
                          </div>
                        )}
                        {c.lastMessagePreview && (
                          <div className="text-[11px] t-muted truncate mt-0.5">
                            {c.lastMessagePreview}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          void onDeleteConversation(c.id)
                        }}
                        className="opacity-0 group-hover:opacity-100 t-faint hover:text-rose-300 p-1"
                        aria-label="Eliminar conversación"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </aside>

        {/* Chat */}
        <section className="surface flex flex-col min-h-0">
          {/* Header del chat con contexto */}
          <div className="border-b border-base px-4 py-2 flex items-center justify-between gap-2">
            <div className="text-sm t-secondary truncate">
              {activeConversation?.title ?? 'Nueva conversación'}
            </div>
            <button
              type="button"
              onClick={() => setShowContext(true)}
              disabled={Boolean(activeId)}
              className="text-xs px-2 py-1 rounded border border-base hover:bg-hover t-secondary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              title={
                activeId
                  ? 'El contexto se fija al crear la conversación'
                  : 'Vincula la conversación a un expediente, cliente o documento'
              }
            >
              <ContextIcon type={contextSel.type} />
              {contextSel.label ?? AiContextTypeLabel[contextSel.type]}
            </button>
          </div>

          {/* Mensajes */}
          <div ref={messagesRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.length === 0 && !streaming && (
              <EmptyChat available={status?.available ?? false} />
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} onPickReference={drawer.show} />
            ))}
            {error && (
              <div className="flex items-start gap-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3">
                <AlertCircle size={14} className="mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-base p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    void onSend()
                  }
                }}
                placeholder={
                  status?.available
                    ? 'Escribe tu pregunta… (Enter envía, Shift+Enter salto de línea)'
                    : 'Asistente IA no configurado. Define ANTHROPIC_API_KEY en el servidor.'
                }
                disabled={!status?.available || streaming}
                rows={2}
                className="flex-1 resize-none bg-transparent border border-base rounded-lg px-3 py-2 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50 disabled:opacity-60"
              />
              {streaming ? (
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-3 py-2 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
                >
                  <Loader2 size={14} className="animate-spin" /> Detener
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void onSend()}
                  disabled={!input.trim() || !status?.available}
                  className="px-3 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  <Send size={14} /> Enviar
                </button>
              )}
            </div>
          </div>
        </section>
      </div>

      {showContext && (
        <ContextPickerModal
          onClose={() => setShowContext(false)}
          onPick={(sel) => {
            setContextSel(sel)
            setShowContext(false)
          }}
        />
      )}

      <CitationDrawer open={drawer.open} reference={drawer.ref} onClose={drawer.hide} />
    </div>
  )
}

// ---------- Subcomponentes ----------

function ContextIcon({ type }: { type: AiContextTypeT }) {
  if (type === 'CASE') return <Briefcase size={11} />
  if (type === 'CLIENT') return <UserIcon size={11} />
  if (type === 'DOCUMENT') return <FileText size={11} />
  return <Sparkles size={11} />
}

function EmptyChat({ available }: { available: boolean }) {
  return (
    <div className="grid place-items-center h-full text-center px-6">
      <div className="space-y-2">
        <Sparkles size={32} className="text-toga-400 mx-auto" />
        <h3 className="text-base font-semibold t-primary">¿En qué te ayudo?</h3>
        <p className="text-xs t-muted max-w-md">
          {available
            ? 'Vincula un expediente o cliente como contexto y pregunta lo que necesites. Puedo redactar escritos, resumir expedientes o buscar precedentes en tu jurisprudencia interna.'
            : 'El asistente está apagado en este entorno. Define ANTHROPIC_API_KEY en el servidor para activarlo.'}
        </p>
      </div>
    </div>
  )
}

function MessageBubble({
  message,
  onPickReference,
}: {
  message: UiMessage
  onPickReference: (ref: ChatReference) => void
}) {
  const isUser = message.role === 'USER'
  const refs = message.references ?? []
  return (
    <div className={['flex', isUser ? 'justify-end' : 'justify-start'].join(' ')}>
      <div
        className={[
          'max-w-[80%] rounded-lg px-3 py-2 text-sm',
          isUser
            ? 'bg-toga-500/15 text-toga-100 border border-toga-500/30'
            : 'bg-surface-2 border border-base t-primary',
        ].join(' ')}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap">{message.content}</div>
        ) : refs.length > 0 ? (
          // Cuando hay refs, render con citas inline (perdemos markdown rico, pero
          // ganamos chips clickables). Para mensajes históricos sin refs, usamos markdown.
          <div className="text-sm leading-relaxed whitespace-pre-wrap">
            {renderWithCitations(
              message.content || (message.pending ? '…' : ''),
              refs,
              onPickReference
            )}
          </div>
        ) : (
          <div className="prose prose-sm prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content || (message.pending ? '…' : '')}
            </ReactMarkdown>
          </div>
        )}
        {refs.length > 0 && (
          <div className="mt-2 pt-2 border-t border-base flex flex-wrap gap-1.5 text-[10px] t-faint">
            <span>Fuentes:</span>
            {refs.map((r) => (
              <button
                key={r.ref}
                type="button"
                onClick={() => onPickReference(r)}
                className="px-1.5 py-0.5 rounded border border-base hover:bg-hover text-[10px]"
                title={r.documentName}
              >
                {r.ref} · {r.documentName}
              </button>
            ))}
          </div>
        )}
        {message.pending && !isUser && (
          <div className="mt-1 flex items-center gap-1 text-[10px] t-faint">
            <Loader2 size={10} className="animate-spin" />
            generando
          </div>
        )}
      </div>
    </div>
  )
}

// ---------- Modal picker de contexto ----------

interface ContextPickerProps {
  onClose: () => void
  onPick: (sel: ContextSelection) => void
}

function ContextPickerModal({ onClose, onPick }: ContextPickerProps) {
  const [tab, setTab] = useState<'CASE' | 'CLIENT' | 'NONE'>('CASE')
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [cases, setCases] = useState<CaseListResponse['items']>([])
  const [clients, setClients] = useState<ClientListResponse['items']>([])

  const load = useCallback(async () => {
    if (tab === 'NONE') return
    setLoading(true)
    try {
      if (tab === 'CASE') {
        const params = new URLSearchParams()
        if (q.trim()) params.set('q', q.trim())
        params.set('pageSize', '40')
        const resp = await api.get<CaseListResponse>(`/cases?${params.toString()}`)
        setCases(resp.items)
      } else {
        const params = new URLSearchParams()
        if (q.trim()) params.set('q', q.trim())
        params.set('pageSize', '40')
        const resp = await api.get<ClientListResponse>(`/clients?${params.toString()}`)
        setClients(resp.items)
      }
    } catch {
      setCases([])
      setClients([])
    } finally {
      setLoading(false)
    }
  }, [tab, q])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl surface-deep p-5 animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold t-primary mb-3">Vincular contexto</h3>
        <div className="inline-flex rounded-lg border border-base overflow-hidden mb-3">
          {(
            [
              { k: 'CASE' as const, label: 'Expediente' },
              { k: 'CLIENT' as const, label: 'Cliente' },
              { k: 'NONE' as const, label: 'Sin contexto' },
            ]
          ).map((t, i) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              className={[
                'px-3 py-1.5 text-xs',
                i > 0 ? 'border-l border-base' : '',
                tab === t.k ? 'bg-toga-500/15 text-toga-200' : 't-secondary hover:bg-hover',
              ].join(' ')}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab !== 'NONE' && (
          <>
            <div className="relative mb-2">
              <Search
                size={14}
                className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
              />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={tab === 'CASE' ? 'Buscar expediente…' : 'Buscar cliente…'}
                className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
              />
            </div>
            <div className="max-h-72 overflow-y-auto -mx-1 px-1">
              {loading ? (
                <div className="text-xs t-faint text-center py-6">Cargando…</div>
              ) : tab === 'CASE' ? (
                cases.length === 0 ? (
                  <div className="text-xs t-faint text-center py-6">Sin resultados</div>
                ) : (
                  <ul className="space-y-1">
                    {cases.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() =>
                            onPick({ type: 'CASE', id: c.id, label: `Expediente ${c.code}` })
                          }
                          className="w-full text-left px-2 py-1.5 rounded hover:bg-hover"
                        >
                          <div className="text-sm t-primary">
                            <span className="font-mono text-[11px] t-faint mr-2">{c.code}</span>
                            {c.title}
                          </div>
                          <div className="text-[10px] t-faint">
                            {c.status} · {c.area}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )
              ) : clients.length === 0 ? (
                <div className="text-xs t-faint text-center py-6">Sin resultados</div>
              ) : (
                <ul className="space-y-1">
                  {clients.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() =>
                          onPick({ type: 'CLIENT', id: c.id, label: `Cliente ${c.displayName}` })
                        }
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-hover"
                      >
                        <div className="text-sm t-primary">{c.displayName}</div>
                        <div className="text-[10px] t-faint">{c.type}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        {tab === 'NONE' && (
          <div className="py-4">
            <p className="text-sm t-secondary">
              Una conversación sin contexto no tendrá acceso a expedientes ni clientes. Útil para
              consultas generales de derecho.
            </p>
            <button
              type="button"
              onClick={() => onPick({ type: 'NONE', id: null, label: null })}
              className="mt-3 px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700"
            >
              Usar sin contexto
            </button>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}
